import type { GQL_ID } from '@@partner:types:graphql'
import type { CrudFormProps, LabelTranslation } from '@@partner:types:app'
import type { LabelFormState, LabelFormTranslation } from './LabelFormState'

import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { mergeLeft, mergeLeftOnly } from 'merge-left-utils'
import { useValidatableForm } from '@@partner:helpers/hooks/rsuite'
import { useFetchSingleLabel } from '@@partner:graphql/query/labels/singleLabel.gql'
import { useMutateSaveLabel } from '@@partner:graphql/mutation/labels/saveLabels.gql'
import { ensureUrlProtocol } from '@@partner:helpers/util'

import {
  notifyOk,
  makeGraphQLErrorNotification,
  notifyError
} from '../../../helpers/notification'
import {
  makeLabelFormState,
  mapApiDataToState,
  mapStateToApiVariable
} from './LabelFormState'
import ValidatorModel from './validators/LabelFormValidationModel'

import {
  Form,
  Grid,
  Toggle,
  Panel,
  Row,
  Col,
  InputGroup,
  DatePicker
} from 'rsuite'
import DropzonePreview from '@components/common/form/DropzonePreview'
import DropzoneUploader from '@components/common/form/DropzoneUploader'
import FormTextareaControl from '@components/common/form/FormTextareaControl'
import FormElementErrorDisplay from '@components/common/form/FormElementErrorDisplay'
import { SaveButton } from '@components/common/form/Buttons'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import FormColorPicker from '@components/common/form/FormColorPicker'
import FormSelect from '@components/common/form/FormSelect'
import LabelPreview from './LabelPreview'

import { API_URL_UPLOAD_LABEL_IMAGE } from '@@partner:config/api'
import SelectLocale from '@components/common/localizedForm/SelectLocale'
import LocalizedForm from '@components/common/localizedForm/LoclalizedForm'

export interface LabelFormProps extends CrudFormProps {
  labelId?: GQL_ID
}

export default function LabelForm ({ labelId, onSaved }: LabelFormProps) {
  const [ formError, setFormError ] = useState({})
  const { formState, setFormState, setFormRef } =
    useValidatableForm<LabelFormState>(makeLabelFormState)
  const [ selectedLanguage, setSelectedLanguage ] = useState<string>()

  const { data, loading, error } = useFetchSingleLabel({
    id: labelId
  })

  const [ saveLabel, { loading: loadingSaveLabel } ] = useMutateSaveLabel()

  const labelSortOrder = [
    { label: '1', value: 1 },
    { label: '2', value: 2 },
    { label: '3', value: 3 },
    { label: '4', value: 4 },
    { label: '5', value: 5 }
  ]

  useEffect(() => {
    if (!loading && data) {
      setFormState((state) => mergeLeft(state, mapApiDataToState(data?.label)))
    }
  }, [ data, loading, setFormState ])

  const handleFormError = useCallback((error: any) => {
    setFormError(error)
  }, [])

  const handleLabelUploaded = useCallback(
    (image_id, url) => {
      setFormState((state) => {
        return mergeLeftOnly([ 'image_id', 'image', 'url' ], state, {
          image_id,
          image: { url }
        })
      })
    },
    [ setFormState ]
  )

  const handleUnlinkLabelClick = useCallback(() => {
    setFormState((state) =>
      mergeLeftOnly([ 'image_id', 'image', 'url' ], state, makeLabelFormState())
    )
  }, [ setFormState ])

  const handleTranslateChange = useCallback(
    (field: keyof LabelFormTranslation) => (value: string) => {
      if (!selectedLanguage) {
        return
      }
      const newTranslations = [ ...formState.i18n ]
      const translationIndex = newTranslations.findIndex(translation => translation.language_code === selectedLanguage)
      if (translationIndex > -1) {
        newTranslations[translationIndex][field] = value
      }
      else {
        const newTranslation = mergeLeft({
          language_code: selectedLanguage,
          name: '',
          description: '',
          option_link_label: '',
          image_id: undefined,
          image: {
            url: ''
          }
        }, {
          language_code: selectedLanguage,
          [field]: value
        })
        newTranslations.push(newTranslation)
      }
      setFormState((state) => mergeLeft(state, { i18n: newTranslations }))
    },
    [ formState.i18n, selectedLanguage, setFormState ]
  )

  
  const handleFormChange = useCallback(
    (formData: any) => {
      setFormState((state) => mergeLeft(state, formData))
    },
    [ setFormState ]
  )

  const handleFormSubmit = useCallback(
    (isOk = false) => {
      if (isOk) {
        saveLabel({
          variables: {
            newData: mapStateToApiVariable(formState),
            upsert: formState.id ? false : true
          }
        })
          .then(({ data }) => {
            notifyOk('Лейбл сохранён')
            setFormState((state) =>
              mergeLeftOnly([ 'id' ], state, data?.labelInfo)
            )

            onSaved?.(data?.labelInfo.id)
          })
          .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
      }
      else {
        notifyError('Форма заполнена некорректно')
      }
    },
    [ saveLabel, formState, setFormState, onSaved ]
  )

  if (error) {
    return (
      <GraphQLCommonErrorDisplay
        title={`Не удалось загрузить лейбл #${labelId} для редактирования`}
        error={error}
      />
    )
  }

  return (
    <Form
      formValue={formState}
      onChange={handleFormChange}
      onSubmit={handleFormSubmit}
      onCheck={handleFormError}
      ref={setFormRef}
      model={ValidatorModel}
      fluid
    >
      <Grid fluid>
        <Col md={14}>
          <Panel
            shaded
            header="Основная информация"
            className='my-4'
          >
            <Row className="my-4"> 
              <Col xs={11}>
                <Form.Group>
                  <Form.ControlLabel>Название</Form.ControlLabel>
                  <Form.Control name="name" placeholder="Название" />
                </Form.Group>
              </Col>{' '}
              <Col xs={11}>
                <Form.Group>
                  <Form.ControlLabel>Техническое название</Form.ControlLabel>
                  <Form.Control
                    name="system_name"
                    placeholder="Техническое название"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="my-4">
              <Col xs={11}>
                <Form.Group>
                  <Form.ControlLabel>Ссылка</Form.ControlLabel>
                  <Form.Control
                    name="optionLink"
                    placeholder="Ссылка"
                    onBlur={(event: any) =>
                      setFormState((state: any) => ({
                        ...state,
                        optionLink: ensureUrlProtocol(event.target.value)
                      }))
                    }
                  />
                </Form.Group>
              </Col>{' '}
              <Col xs={11}>
                <Form.Group>
                  <Form.ControlLabel>Текст ссылки</Form.ControlLabel>
                  <Form.Control
                    name="optionLinkLabel"
                    placeholder="Текст ссылки"
                  />
                </Form.Group>
              </Col>
            </Row>{' '}
            <Row className="my-4">
              <Col xs={22}>
                <Form.Group>
                  <Form.ControlLabel>Описание</Form.ControlLabel>
                  <FormTextareaControl
                    placeholder="Описание"
                    name="description"
                    rows={5}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Panel>
          <Panel
            shaded
            header="Переводы"
            className="my-4"
          >

            <SelectLocale
              selectedLanguage={selectedLanguage}
              onSelect={(language) => setSelectedLanguage(language)}
              onAdd={(language) => {
                setFormState((state) => mergeLeft(state, { languages: [ ...state.languages, language ] }))
              }}
              languages={formState.languages}
            />

            { selectedLanguage && (
              <div>
                <LocalizedForm
                  name='name'
                  label='Название'
                  selectedLanguage={selectedLanguage}
                  value={formState.i18n?.find((translation) => translation.language_code === selectedLanguage)?.name || ''}
                  onChange={handleTranslateChange('name')}
                />
                <LocalizedForm
                  name='description'
                  type='textarea'
                  label='Описание'
                  selectedLanguage={selectedLanguage}
                  value={formState.i18n?.find((translation) => translation.language_code === selectedLanguage)?.description || ''}
                  onChange={handleTranslateChange('description')}
                />
                <LocalizedForm
                  name='option_link_label'
                  label='Текст ссылки'
                  selectedLanguage={selectedLanguage}
                  value={formState.i18n?.find((translation) => translation.language_code === selectedLanguage)?.optionLinkLabel || ''}
                  onChange={handleTranslateChange('option_link_label')}
                />
              </div>
            )}
          </Panel>
          <Panel
            shaded
            header="Дополнительные параметры"
            className="my-4"
          >
            <Row className="my-4">
              <Col xs={11}>
                <Form.Group>
                  <Form.ControlLabel>Активен</Form.ControlLabel>

                  <Form.Control
                    accepter={Toggle}
                    size="lg"
                    name="status"
                    checkedChildren="АКТИВЕН"
                    unCheckedChildren="СКРЫТ"
                    checked={formState.status ? true : false}
                    value={formState.status ? true : false}
                    onChange={(value) =>
                      setFormState((state) => ({
                        ...state,
                        status: value === true ? 1 : 0
                      }))
                    }
                  />
                </Form.Group>
              </Col>{' '}
              <Col xs={11}>
                <Form.Group>
                  <Form.ControlLabel>Порядок сортировки</Form.ControlLabel>
                  <FormSelect name="sort_order" data={labelSortOrder} />
                </Form.Group>
              </Col>
            </Row>{' '}
            <Row className="my-4">
              <Col xs={22}>
                <Form.ControlLabel>Период действия</Form.ControlLabel>

                <InputGroup>
                  <Form.Control
                    accepter={DatePicker}
                    block
                    oneTap
                    isoWeek
                    appearance="subtle"
                    name="activeFrom"
                    value={formState.activeFrom}
                    format="yyyy-MM-dd HH:mm:ss"
                    placeholder="дата начала действия"
                  />

                  <InputGroup.Addon> до </InputGroup.Addon>

                  <Form.Control
                    accepter={DatePicker}
                    block
                    oneTap
                    isoWeek
                    appearance="subtle"
                    name="activeUntil"
                    value={formState.activeUntil}
                    format="yyyy-MM-dd HH:mm:ss"
                    placeholder="дата окончания действия"
                  />
                </InputGroup>
              </Col>
            </Row>
            <Row className="my-4">
              <Col xs={11}>
                <Form.Group>
                  <Form.ControlLabel>Цвет текста</Form.ControlLabel>
                  <FormColorPicker
                    name="optionColor"
                    inputClassName="w-32 h-12 p-1"
                  />

                  <Form.HelpText>(HEX: {formState.optionColor})</Form.HelpText>
                </Form.Group>
              </Col>{' '}
              <Col xs={11}>
                <Form.Group>
                  <Form.ControlLabel>Цвет фона</Form.ControlLabel>
                  <FormColorPicker
                    name="optionBackgroundColor"
                    inputClassName="w-32 h-12 p-1"
                  />

                  <Form.HelpText>
                    (HEX: {formState.optionBackgroundColor})
                  </Form.HelpText>
                </Form.Group>
              </Col>
              <Col xs={22}>
                <h2>Предпросмотр</h2>
                <LabelPreview data={formState} />
              </Col>
            </Row>
          </Panel>
        </Col>
        <Col
          md={9}
          className="text-center border rounded-md py-4 px-2 text-lg"
        >
          <Form.Group>
            <Form.ControlLabel>Лейбл</Form.ControlLabel>
            <FormElementErrorDisplay field="image_id" error={formError} />

            <DropzoneUploader
              action={API_URL_UPLOAD_LABEL_IMAGE}
              name="marketing-label-image"
              onUploaded={handleLabelUploaded}
              multiple={false}
            />
          </Form.Group>
          <Form.Group>
            {formState.image?.url && (
              <DropzonePreview
                url={formState.image.url}
                onDelete={handleUnlinkLabelClick}
                imageClassName="w-full"
              />
            )}
          </Form.Group>
        </Col>
      </Grid>
      <SaveButton
        loading={loading || loadingSaveLabel}
        disabled={loading || loadingSaveLabel}
      >
        Сохранить
      </SaveButton>
    </Form>
  )
}
